/**
 * @module input/gestureEngine
 * Detects slash gestures from smoothed fingertip positions.
 *
 * A slash is registered when the pointer's instantaneous velocity exceeds
 * CONFIG.slash.minVelocity. The resulting "slash segment" (previous → current
 * position) is passed to the collision system for fruit intersection tests.
 *
 * ADR-003: processFrame() returns [] when StateMachine.currentState !== 'PLAYING'.
 * The caller (GameLoop) enforces this gate — gestureEngine does not import
 * StateMachine to avoid circular dependencies in Phase 0 PoC wiring.
 *
 * ADR-001: Slash detection runs in the tracking loop, NOT in rAF, so velocity
 * is computed from real tracking deltas rather than interpolated positions.
 */

import type { HandPointer, SlashSegment } from '../types.js';
import { CONFIG } from '../game-config.js';

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

/** Previous pointer position per hand index. */
const _prevPointer: [HandPointer | null, HandPointer | null] = [null, null];

/**
 * Consecutive fast samples per hand. Tracking jitter and landmark re-locks
 * produce SINGLE-sample velocity spikes; an intentional slash stays fast
 * across consecutive samples. Segments are suppressed until the streak
 * reaches CONFIG.slash.minStreakSamples — kills phantom "self-slicing".
 */
const _fastStreak: [number, number] = [0, 0];

/**
 * Per-hand buffer of fast segments accumulated while streak < minStreakSamples.
 * When the streak reaches the threshold, buffered segments are flushed first
 * (chronological order) so no leading segment is lost. A slow sample clears
 * the buffer, preserving the anti-jitter guarantee.
 */
const _pendingSegments: [SlashSegment[], SlashSegment[]] = [[], []];

/** Registered slash listeners. */
const _slashHandlers: Array<(segment: SlashSegment, velocity: number) => void> = [];

// ---------------------------------------------------------------------------
// Public functional API (per module-contracts.md)
// ---------------------------------------------------------------------------

/**
 * Process one tracking frame's pointer data.
 * Returns slash segments generated this frame.
 * Returns [] when called by the game loop with state !== 'PLAYING'.
 *
 * @param pointers - Smoothed hand pointers from `landmarksToPointers`.
 * @param isPlaying - Pass `true` only when StateMachine.currentState === 'PLAYING'.
 */
export function processFrame(
  pointers: readonly HandPointer[],
  isPlaying = true,
): SlashSegment[] {
  if (!isPlaying) {
    // Per ADR-003: no slash segments outside PLAYING state.
    return [];
  }

  const segments: SlashSegment[] = [];

  for (const pointer of pointers) {
    const prev = _prevPointer[pointer.handIndex];
    _prevPointer[pointer.handIndex] = pointer;

    if (!prev) continue;

    const dt = (pointer.timestamp - prev.timestamp) / 1000;
    if (dt <= 0) continue;

    const dx = pointer.x - prev.x;
    const dy = pointer.y - prev.y;
    const velocity = Math.hypot(dx, dy) / dt;

    if (velocity < CONFIG.slash.minVelocity) {
      _fastStreak[pointer.handIndex] = 0;
      // Clear pending buffer — a slow sample breaks the sustained-motion
      // requirement, so any buffered pre-threshold segments must be discarded
      // to preserve the anti-jitter guarantee.
      _pendingSegments[pointer.handIndex] = [];
      continue;
    }

    // Sustained-motion gate (see _fastStreak doc above).
    _fastStreak[pointer.handIndex] += 1;

    const segment: SlashSegment = {
      from: { x: prev.x, y: prev.y },
      to: { x: pointer.x, y: pointer.y },
      velocity,
      handIndex: pointer.handIndex,
    };

    if (_fastStreak[pointer.handIndex] < CONFIG.slash.minStreakSamples) {
      // Buffer segment until the streak is confirmed; do not emit yet.
      _pendingSegments[pointer.handIndex].push(segment);
      continue;
    }

    // Streak confirmed: flush pending buffer first (chronological order),
    // then emit the current segment.
    const pending = _pendingSegments[pointer.handIndex];
    _pendingSegments[pointer.handIndex] = [];

    const toEmit = [...pending, segment];
    for (const seg of toEmit) {
      segments.push(seg);
      // Notify listeners synchronously.
      for (const handler of _slashHandlers) {
        handler(seg, seg.velocity);
      }
    }
  }

  return segments;
}

/**
 * Register a callback invoked synchronously each time a slash is detected.
 * Returns an unsubscribe function.
 */
export function onSlash(
  handler: (segment: SlashSegment, velocity: number) => void,
): () => void {
  _slashHandlers.push(handler);
  return () => {
    const idx = _slashHandlers.indexOf(handler);
    if (idx !== -1) _slashHandlers.splice(idx, 1);
  };
}

/**
 * Clear all slash state (call on state transition away from PLAYING).
 * Per ADR-003: called synchronously from the onStateChange handler to prevent
 * buffered ghost events surviving the transition.
 */
export function resetSlashState(): void {
  _prevPointer[0] = null;
  _prevPointer[1] = null;
  _fastStreak[0] = 0;
  _fastStreak[1] = 0;
  _pendingSegments[0] = [];
  _pendingSegments[1] = [];
}

// ---------------------------------------------------------------------------
// Legacy class-based API (backwards compat with main.ts PoC)
// ---------------------------------------------------------------------------

/** A directed line segment representing a detected slash (legacy). */
export interface LegacySlashSegment {
  from: { x: number; y: number };
  to: { x: number; y: number };
  velocity: number;
}

/**
 * @deprecated Use the functional API (processFrame, onSlash, resetSlashState).
 * Kept for backwards compat with the PoC main.ts.
 */
export class GestureEngine {
  private _prev: { x: number; y: number } | undefined;
  private _prevTime: number | undefined;

  update(
    pos: { x: number; y: number },
    timestamp: number,
    minVelocity: number,
  ): LegacySlashSegment | null {
    const prev = this._prev;
    const prevTime = this._prevTime;

    this._prev = pos;
    this._prevTime = timestamp;

    if (prev === undefined || prevTime === undefined) return null;

    const dt = (timestamp - prevTime) / 1000;
    if (dt <= 0) return null;

    const dx = pos.x - prev.x;
    const dy = pos.y - prev.y;
    const velocity = Math.hypot(dx, dy) / dt;

    if (velocity < minVelocity) return null;

    return { from: prev, to: pos, velocity };
  }

  reset(): void {
    this._prev = undefined;
    this._prevTime = undefined;
  }
}
